import type {
  AnimationClip,
  BufferAttribute,
  Group,
  LoadingManager,
  Mesh,
  Object3D,
} from '@seddi/three';
import { GLTFLoader } from '@seddi/three/examples/jsm/loaders/GLTFLoader.js';
import log from 'loglevel';
import { transformUvsDirectXToOpenGL } from '../core/bufferAttribute';
import { type ModelDesc, ModelFormat } from './modelTypes';

type GLTFLoaderRes = {
  animations: AnimationClip[];
  asset: any;
  cameras: any;
  parser: any;
  scene: Group;
  scenes: Group[];
  userData: any;
};

const processMeshDecals = (root: Object3D, ids: string[]) => {
  const pending = [root];
  let i = 0;

  while (i < pending.length) {
    const object = pending[i];

    if ((object as any).isMesh) {
      // Use original name instead of glTF loader modified name
      const name = object.userData.name ?? object.name;

      if (ids.includes(name)) {
        const mesh = object as Mesh;
        // Transform UVs, assume glTF follows standard then UV space is in DX format
        transformUvsDirectXToOpenGL(mesh.geometry.attributes['uv'] as BufferAttribute);
        // Reset materials
        mesh.material = [];
        mesh.userData.isMeshDecal = true;
      }
    }

    pending.push(...object.children);
    i++;
  }
};

/**
 * Load model.
 * @param desc - Model.
 * @param manager - Loading manager.
 */
export const load = async (
  desc: ModelDesc,
  manager: LoadingManager,
): Promise<Group | undefined> => {
  switch (desc.format) {
    case ModelFormat.GLTF: {
      // Load the GLTF file with GLTFLoader extension
      const loader = new GLTFLoader(manager);

      return new Promise((resolve) => {
        loader.load(desc.href, function (gltf: GLTFLoaderRes) {
          // Mesh decals postprocess
          if (desc.meta?.meshDecals) {
            processMeshDecals(gltf.scene, desc.meta.meshDecals);
          }
          // Resolve Three.js group
          resolve(gltf.scene);
        });
      });
    }
    default: {
      log.error(`Unrecognized model format ${desc.format}`);
      return undefined;
    }
  }
};
